import React, { useEffect, useMemo, useState } from 'react';
import { FileText } from 'lucide-react';

const thumbnailCache = new Map();

const isAbortError = (error) => error?.name === 'AbortError';

const buildAuthorizedUrl = (fileUrl, token) => {
    if (!fileUrl) return '';
    if (!token) return fileUrl;

    const separator = fileUrl.includes('?') ? '&' : '?';
    return `${fileUrl}${separator}token=${encodeURIComponent(token)}`;
};

const ensurePdfJsCompat = () => {
    if (typeof Promise.withResolvers !== 'function') {
        Promise.withResolvers = function withResolvers() {
            let resolve;
            let reject;
            const promise = new Promise((res, rej) => {
                resolve = res;
                reject = rej;
            });
            return { promise, resolve, reject };
        };
    }
};

const ensurePdfJsWorker = async () => {
    if (typeof window !== 'undefined' && window.pdfjsWorker?.WorkerMessageHandler) {
        return;
    }

    try {
        const workerModule = await import('pdfjs-dist/build/pdf.worker.min.mjs');
        if (typeof window !== 'undefined') {
            window.pdfjsWorker = workerModule?.default || workerModule;
        }
    } catch {
        const workerModule = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
        if (typeof window !== 'undefined') {
            window.pdfjsWorker = workerModule?.default || workerModule;
        }
    }
};

const loadPdfJs = async () => {
    ensurePdfJsCompat();

    let pdfjsLib = null;
    try {
        pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
    } catch {
        pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
    }

    await ensurePdfJsWorker();
    return pdfjsLib;
};

const getCacheKey = ({ file, fileUuid, fileName, width }) => {
    if (file instanceof File) {
        return `local:${file.name}:${file.size}:${file.lastModified}:${width}`;
    }
    if (fileUuid) {
        return `remote:${fileUuid}:${width}`;
    }
    return `fallback:${fileName || 'pdf'}:${width}`;
};

const fetchPdfData = async ({ file, fileUrl, token, signal }) => {
    if (file instanceof File) {
        return file.arrayBuffer();
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(buildAuthorizedUrl(fileUrl, token), { headers, signal });
    if (!response.ok) {
        throw new Error('No se pudo cargar el PDF');
    }
    return response.arrayBuffer();
};

const renderPdfThumbnail = async ({ file, fileUrl, token, width, signal }) => {
    const pdfjsLib = await loadPdfJs();
    const data = await fetchPdfData({ file, fileUrl, token, signal });

    if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
    }

    const loadingTask = pdfjsLib.getDocument({
        data,
        disableWorker: true,
        isEvalSupported: false
    });

    let pdf = null;
    let page = null;

    try {
        pdf = await loadingTask.promise;
        page = await pdf.getPage(1);

        const baseViewport = page.getViewport({ scale: 1 });
        const scale = width / baseViewport.width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { alpha: false });

        if (!context) {
            throw new Error('No se pudo crear el canvas del thumbnail');
        }

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({
            canvasContext: context,
            viewport
        }).promise;

        return canvas.toDataURL('image/jpeg', 0.9);
    } finally {
        page?.cleanup?.();
        pdf?.cleanup?.();
        loadingTask.destroy?.();
    }
};

const PdfThumbnail = ({
    file,
    fileUuid,
    fileUrl,
    token,
    fileName,
    width = 160,
    className = '',
    imageClassName = '',
    badgeClassName = '',
    placeholderClassName = ''
}) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [hasError, setHasError] = useState(false);

    const cacheKey = useMemo(
        () => getCacheKey({ file, fileUuid, fileName, width }),
        [file, fileName, fileUuid, width]
    );

    useEffect(() => {
        const cachedThumbnail = thumbnailCache.get(cacheKey);
        if (cachedThumbnail) {
            setThumbnailUrl(cachedThumbnail);
            setHasError(false);
            return;
        }

        const abortController = new AbortController();
        let cancelled = false;

        setThumbnailUrl(null);
        setHasError(false);

        renderPdfThumbnail({
            file,
            fileUrl,
            token,
            width,
            signal: abortController.signal
        })
            .then((dataUrl) => {
                if (cancelled || !dataUrl) return;
                thumbnailCache.set(cacheKey, dataUrl);
                setThumbnailUrl(dataUrl);
            })
            .catch((error) => {
                if (cancelled || isAbortError(error)) return;
                console.error('Error generando thumbnail de PDF:', error);
                setHasError(true);
            });

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [cacheKey, file, fileUrl, token, width]);

    return (
        <div className={className}>
            {thumbnailUrl ? (
                <img
                    src={thumbnailUrl}
                    alt={fileName || 'PDF'}
                    className={imageClassName}
                    loading="lazy"
                />
            ) : (
                <div className={placeholderClassName}>
                    <FileText size={24} />
                    <span>{hasError ? 'PDF' : 'Cargando'}</span>
                </div>
            )}
            <span className={badgeClassName}>PDF</span>
        </div>
    );
};

export default PdfThumbnail;
